// Router adapter – abstrakce nad TanStack Routerem pro navigační jádro

import type { HistoryAction } from './historyPolicy';

/** Abstrakce nad router API – navigační jádro nezná TanStack Router přímo */
export interface RouterAdapter {
  /** Vrátí aktuální search params z URL */
  getSearchParams(): URLSearchParams;
  /** Nastaví nové search params do URL */
  setSearchParams(params: URLSearchParams, action: HistoryAction): void;
  /** Přihlásí se k notifikacím o změnách URL */
  subscribe(callback: () => void): () => void;
}

/**
 * Vytvoří router adapter nad TanStack Router instancí.
 * Navigační jádro komunikuje s routerem pouze přes toto rozhraní.
 */
export function createTanStackRouterAdapter(router: {
  navigate: (opts: {
    search: Record<string, string>;
    hash?: string;
    replace?: boolean;
  }) => Promise<void>;
  subscribe: (event: string, callback: () => void) => () => void;
  state: { location: { search: Record<string, string> } };
}): RouterAdapter {
  return {
    getSearchParams() {
      // TanStack Router ukládá search jako objekt a primitiva (čísla, booleany)
      // ze syrové URL parsuje na nativní typy. Pro náš stringově orientovaný
      // navigation kernel je proto coercnem zpět na string, aby šly obnovit
      // deep-linky s number/boolean parametry agend.
      const search = router.state.location.search;
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(search)) {
        if (value == null) continue;
        if (typeof value === 'object') continue;
        params.set(key, String(value));
      }
      return params;
    },

    setSearchParams(params: URLSearchParams, action: HistoryAction) {
      const searchObj: Record<string, string> = {};
      for (const [key, value] of params.entries()) {
        searchObj[key] = value;
      }
      // Zachovat aktuální URL fragment (`#…`) — drží UI layout (windowing) a kernelová
      // query-navigace ho nesmí setřít. Předáváme jen když není prázdný.
      const currentHash =
        typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
      void router.navigate({
        search: searchObj,
        ...(currentHash ? { hash: currentHash } : {}),
        replace: action === 'replace',
      });
    },

    subscribe(callback: () => void) {
      return router.subscribe('onResolved', callback);
    },
  };
}
